import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LoadingSpinner } from '@/components/ui';
import { styled, keyframes } from '@mui/material/styles';

// Success animation keyframes
const successPulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SuccessContainer = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  animation: `${fadeIn} 0.5s ease-out`,
}));

const SuccessCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
  textAlign: 'center',
  maxWidth: 400,
  width: '90%',
  animation: `${fadeIn} 0.6s ease-out 0.2s both`,
}));

const SuccessIcon = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  backgroundColor: '#4caf50',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  animation: `${successPulse} 2s ease-in-out infinite`,
  '&::before': {
    content: '"✓"',
    color: 'white',
    fontSize: '2rem',
    fontWeight: 'bold',
  },
}));

interface LoginSuccessOverlayProps {
  /** Duration to show the success message before redirecting */
  duration?: number;
  /** Custom success message */
  message?: string;
  /** Custom redirect message */
  redirectMessage?: string;
  /** Callback when redirect should happen */
  onRedirect: () => void;
}

export const LoginSuccessOverlay: React.FC<LoginSuccessOverlayProps> = ({
  duration = 2000,
  message = 'Login Successful!',
  redirectMessage = 'Redirecting to dashboard...',
  onRedirect,
}) => {
  const [showRedirect, setShowRedirect] = useState(false);

  useEffect(() => {
    
    // Show redirect message after half the duration
    const redirectTimer = setTimeout(() => {
      setShowRedirect(true);
    }, duration / 2);

    // Trigger redirect after full duration
    const redirectTimer2 = setTimeout(() => {
      console.log('Calling onRedirect...');
      onRedirect();
    }, duration);

    return () => {
      clearTimeout(redirectTimer);
      clearTimeout(redirectTimer2);
    };
  }, [duration, onRedirect]);

  return (
    <SuccessContainer>
      <SuccessCard>
        <SuccessIcon />
        
        <Typography
          variant="h4"
          sx={{
            color: '#4caf50',
            fontWeight: 600,
            marginBottom: 2,
            fontSize: { xs: '1.5rem', sm: '2rem' },
          }}
        >
          {message}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#666',
            marginBottom: 3,
            fontSize: '1rem',
          }}
        >
          Welcome back! You have successfully signed in.
        </Typography>

        {showRedirect && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <LoadingSpinner
              size="small"
              color="#3b82f6"
              title=""
              subtitle=""
            />
            <Typography
              variant="body2"
              sx={{
                color: '#3b82f6',
                fontWeight: 500,
              }}
            >
              {redirectMessage}
            </Typography>
          </Box>
        )}
      </SuccessCard>
    </SuccessContainer>
  );
};

export default LoginSuccessOverlay;
